import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Loader2, X } from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import { useMapStore } from '@/store/map.store';
import workspaceService, { CompanyLayer } from '@/services/workspace.service';
import layerAdmin from '@/services/layerAdmin.service';
import layerStylesService, { type LayerKind, type LayerLook, type LayerStyleInfo, type StyleField } from '@/services/layerStyles.service';
import type { Layer } from '@/types/api';
import { hide, isShown, preview, show } from '@/utils/companyLayers';
import { platformSources, platformViewChanged, setPlatformPreview } from '@/utils/platformViews';
import {
  categoryClasses, classLabel, coloursOf, DEFAULT_FILL, DEFAULT_OTHER, DEFAULT_STROKE, edgesFor, graduatedClasses,
  isNumericType, legendItems, METHODS, RAMPS, rangeLabel, scaleToZoom, suggestedMode, zoomToScale,
  type FieldStats, type LayerStyle, type StyleClass, type StyleMode,
} from '@/utils/layerStyle';
import { inputClass as input, labelClass as label, Slider, SymbolControls } from './SymbolControls';
import { StyleSwatch } from './StyleSwatch';

/**
 * The look of a layer - a company's own (drawn in the browser) or one of ours (drawn by GeoServer) - as the common GIS
 * tools let it be changed: one look, categories of a field, graduated colours or proportional symbols of a number, a
 * heatmap of points; the symbol by the layer's geometry (marker, line, area); labels and the zooms it is drawn between.
 *
 * Everyone who sees a layer may keep their own view of it ("My view", the default); those allowed also change its main
 * look, which everyone else sees ("Main style"). What is changed is shown on the map at once; Cancel puts back what was
 * shown. The classes are made for you from the field's values (smart mapping) and can be changed one by one.
 */

const small = 'p-1 rounded hover:bg-gn-tertiary disabled:opacity-40';

interface Props {
  /** A company layer (from the workspace's list). */
  layer?: CompanyLayer;
  /** Or one of ours (from the layer tree). */
  platformLayer?: Layer;
  lang: string;
  onClose: () => void;
  onSaved?: (layer: CompanyLayer) => void;
}

type Tab = 'mine' | 'main';

function fieldName(f: StyleField, lang: string): string {
  return f.labels?.[lang] || f.labels?.en || f.column || f.key;
}

/** Our layer's name as GeoServer has it: workspace:name. */
export function platformRef(l: Layer): string {
  return `${l.layerWorkspace?.name || 'cite'}:${l.layerName}`;
}

export const LayerStyleEditor: React.FC<Props> = ({ layer: company, platformLayer, lang, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const map = useMapStore((s) => s.mapInstance);
  const locale = i18n.language || 'en';
  const kind: LayerKind = company ? 'company' : 'platform';
  const ref = company ? company.key : platformRef(platformLayer!);

  const { data: info, error: infoError } = useQuery({
    queryKey: ['layerStyle', kind, ref],
    queryFn: () => layerStylesService.info(kind, ref),
    staleTime: 0,
  });
  const geometry = info?.geometry ?? company?.geometry ?? null;
  const fields = useMemo(() => info?.fields ?? [], [info]);
  const numeric = useMemo(() => fields.filter((f) => isNumericType(f.type)), [fields]);
  const modes = (info?.modes ?? ['simple']) as StyleMode[];

  const [tab, setTab] = useState<Tab>('mine');
  const [style, setStyle] = useState<LayerStyle>({});
  const [labelField, setLabelField] = useState<string>('');
  const [minZoom, setMinZoom] = useState<string>('');
  const [maxZoom, setMaxZoom] = useState<string>('');
  const [top, setTop] = useState(10);
  const wasShown = useRef<boolean | null>(null);
  const [ready, setReady] = useState(false);
  const touched = useRef(false);

  /** Starts editing a look: the person's view, or the main look; a look GeoServer keeps starts afresh. */
  const begin = (from: LayerLook | null | undefined, i: LayerStyleInfo) => {
    const own = from === i.main && i.mainEditable === false ? null : from?.style ?? null;
    const s: LayerStyle = { ...(own ?? {}) };
    if (s.mode === 'classes') s.mode = (s.classes ?? []).some((c) => c.from != null) ? 'graduated' : 'categories';
    if (s.mode && !i.modes.includes(s.mode as string)) s.mode = 'simple';
    const point = i.geometry === 'point';
    setStyle({ mode: 'simple', fill: DEFAULT_FILL, stroke: DEFAULT_STROKE, fillOpacity: point ? 0.9 : i.geometry === 'line' ? 1 : 0.55,
      size: 8, marker: 'circle', ...(i.geometry === 'line' ? { lineWidth: 2 } : {}), ...s });
    setLabelField(from?.label ?? '');
    setMinZoom(from?.minScale ? String(scaleToZoom(from.minScale)) : '');
    setMaxZoom(from?.maxScale ? String(scaleToZoom(from.maxScale)) : '');
    if (s.mode === 'categories') setTop(Math.max(1, (s.classes ?? []).length));
    madeFor.current = '';
    touched.current = false;
  };

  useEffect(() => {
    if (!info || ready) return;
    const first: Tab = info.canKeepView ? 'mine' : 'main';
    setTab(first);
    begin(first === 'mine' ? info.mine ?? info.main : info.main, info);
    if (company) wasShown.current = isShown(map, company);
    setReady(true);
  }, [info]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseTab = (next: Tab) => {
    if (!info || next === tab) return;
    setTab(next);
    begin(next === 'mine' ? info.mine ?? info.main : info.main, info);
  };

  const mode = (style.mode ?? 'simple') as StyleMode;
  const by = style.by ?? '';
  const byField = fields.find((f) => f.key === by);
  const statsOf = (field: string) => (company
    ? workspaceService.fieldStats(company.workspace, company.key, field)
    : layerStylesService.fieldStats(kind, ref, field));
  const { data: stats, isFetching: loadingStats } = useQuery<FieldStats>({
    queryKey: ['fieldStats', kind, ref, by],
    queryFn: () => statsOf(by),
    enabled: !!by && mode !== 'simple' && mode !== 'heatmap',
    staleTime: 60_000,
  });
  const weightBy = style.weightBy ?? '';
  const { data: weightStats } = useQuery<FieldStats>({
    queryKey: ['fieldStats', kind, ref, weightBy],
    queryFn: () => statsOf(weightBy),
    enabled: mode === 'heatmap' && !!weightBy,
    staleTime: 60_000,
  });

  const look = useMemo<LayerLook>(() => ({
    style: { ...style, ...(mode === 'categories' ? { method: null, classCount: (style.classes ?? []).length } : {}) },
    label: labelField || null,
    minScale: minZoom !== '' ? zoomToScale(Number(minZoom)) : null,
    maxScale: maxZoom !== '' ? zoomToScale(Number(maxZoom)) : null,
  }), [style, mode, labelField, minZoom, maxZoom]);

  // Live on the map while it is changed: a company layer drawn here, one of ours by GeoServer through a token.
  useEffect(() => {
    if (!map || !ready) return;
    if (company) {
      preview(map, { ...company, style: look.style, label: look.label, minScale: look.minScale, maxScale: look.maxScale });
      return;
    }
    if (!touched.current || platformSources(map, ref).length === 0) return;
    const timer = window.setTimeout(() => {
      layerStylesService.preview(ref, look).then((token) => setPlatformPreview(map, ref, token)).catch(() => undefined);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [map, look, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<LayerStyle>) => {
    touched.current = true;
    setStyle((s) => ({ ...s, ...patch }));
  };

  /* ── making the classes ── */
  const rebuildCategories = (st: FieldStats | undefined, n: number, palette?: string) => {
    if (!st) return;
    const p = palette ?? (style.ramp && RAMPS.find((r) => r.name === style.ramp)?.kind === 'qualitative' ? style.ramp : 'bold');
    set({ classes: categoryClasses(st.values, n, p, lang), ramp: p, otherFill: st.distinct > n ? (style.otherFill ?? DEFAULT_OTHER) : style.otherFill });
  };
  const rebuildGraduated = (st: FieldStats | undefined, method: string, k: number, rampName: string, reverse: boolean) => {
    const edges = edgesFor(st, method, k);
    if (edges.length < 2) return;
    set({ classes: graduatedClasses(edges, rampName, reverse, locale, lang), method, classCount: k, ramp: rampName, reverse });
  };

  // When the field's statistics arrive, the classes are made for it (unless they are already its own).
  const madeFor = useRef<string>('');
  useEffect(() => {
    if (!stats || madeFor.current === `${mode}:${stats.field}`) return;
    const already = (style.classes ?? []).length > 0 && madeFor.current === '' && style.by === stats.field;
    madeFor.current = `${mode}:${stats.field}`;
    if (already) return;
    if (mode === 'categories') rebuildCategories(stats, top);
    if (mode === 'graduated') {
      const ramp = style.ramp && RAMPS.find((r) => r.name === style.ramp)?.kind !== 'qualitative' ? style.ramp : 'blues';
      rebuildGraduated(stats, style.method && style.method !== 'manual' ? style.method : 'quantile', style.classCount ?? 5, ramp, !!style.reverse);
    }
    if (mode === 'proportional' && stats.number) {
      set({ valueMin: stats.number.min ?? 0, valueMax: stats.number.max ?? 1, sizeMin: style.sizeMin ?? (geometry === 'line' ? 1 : 4),
        sizeMax: style.sizeMax ?? (geometry === 'line' ? 12 : 30) });
    }
  }, [stats, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === 'heatmap' && weightStats?.number) set({ valueMin: weightStats.number.min ?? 0, valueMax: weightStats.number.max ?? 1 });
  }, [weightStats, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseMode = (m: StyleMode) => {
    const next: Partial<LayerStyle> = { mode: m };
    if (m === 'categories' || m === 'graduated' || m === 'proportional') {
      const suitable = m === 'categories' ? fields : numeric;
      if (!suitable.some((f) => f.key === by)) next.by = suitable[0]?.key ?? null;
      next.classes = m === mode ? style.classes : [];
    }
    madeFor.current = 'x';
    set(next);
  };

  const chooseField = (key: string) => {
    madeFor.current = 'x';
    set({ by: key, classes: [] });
  };

  const moveClass = (i: number, d: -1 | 1) => {
    const cs = [...(style.classes ?? [])];
    const j = i + d;
    if (j < 0 || j >= cs.length) return;
    [cs[i], cs[j]] = [cs[j], cs[i]];
    set({ classes: cs });
  };
  const changeClass = (i: number, patch: Partial<StyleClass>) => {
    const cs = [...(style.classes ?? [])];
    cs[i] = { ...cs[i], ...patch };
    set({ classes: cs });
  };

  /* ── saving ── */
  const fail = (e: any) => addToast(e?.response?.data?.detail ?? e?.response?.data?.error ?? e?.response?.data?.message ?? e?.message ?? String(e), 'error');
  const refreshed = (mine: CompanyLayer['mine']) => {
    queryClient.invalidateQueries({ queryKey: ['layerStyle', kind, ref] });
    if (company) {
      queryClient.invalidateQueries({ queryKey: ['workspaceLayers', company.workspace] });
      const changed = { ...company, mine };
      if (map) show(map, changed);
      onSaved?.(changed);
    }
  };

  const saveMine = useMutation({
    mutationFn: () => layerStylesService.keep(kind, ref, look),
    onSuccess: (saved) => {
      if (!company) platformViewChanged(map, ref, String(Date.parse(saved.mine?.updatedAt ?? '') || Date.now()));
      refreshed(saved.mine);
      addToast(t('styles.saved_mine'), 'success');
      onClose();
    },
    onError: fail,
  });

  const resetMine = useMutation({
    mutationFn: () => layerStylesService.reset(kind, ref),
    onSuccess: () => {
      if (!company) platformViewChanged(map, ref, null);
      refreshed(null);
      addToast(t('styles.reset_done'), 'success');
      onClose();
    },
    onError: fail,
  });

  const saveMain = useMutation({
    mutationFn: async () => {
      if (company) {
        return workspaceService.change(company.workspace, company.key, {
          style: look.style ?? undefined, label: look.label ?? null, minScale: look.minScale ?? null, maxScale: look.maxScale ?? null,
        });
      }
      await layerAdmin.change(info!.key, { style: look.style as any, label: look.label ?? null, minScale: look.minScale ?? null, maxScale: look.maxScale ?? null });
      return null;
    },
    onSuccess: (saved) => {
      if (company && saved) {
        queryClient.invalidateQueries({ queryKey: ['layerStyle', kind, ref] });
        queryClient.invalidateQueries({ queryKey: ['workspaceLayers', company.workspace] });
        if (map) show(map, saved);
        onSaved?.(saved);
      } else {
        // GeoServer has the new main style: asked for again, past what the browser keeps
        platformViewChanged(map, ref, info?.mine ? String(Date.now()) : null);
        if (!info?.mine) for (const src of platformSources(map, ref)) src.updateParams({ GXV: `main-${Date.now()}` });
        queryClient.invalidateQueries({ queryKey: ['layerStyle', kind, ref] });
      }
      addToast(info?.mine ? t('styles.saved_main_yours_kept') : t('styles.saved_main'), 'success');
      onClose();
    },
    onError: fail,
  });

  const cancel = () => {
    if (map && company) {
      if (wasShown.current) show(map, company);
      else hide(map, company);
    }
    if (map && !company) setPlatformPreview(map, ref, null);
    onClose();
  };

  const currentZoom = () => {
    const z = map?.getView().getZoom();
    return z === undefined ? '' : String(Math.round(z * 10) / 10);
  };

  const zoomError = minZoom !== '' && maxZoom !== '' && Number(minZoom) > Number(maxZoom);
  const classed = mode === 'categories' || mode === 'graduated';
  const rampKinds = mode === 'categories' ? ['qualitative'] : ['sequential', 'diverging'];
  const busy = saveMine.isPending || resetMine.isPending || saveMain.isPending;
  const invalid = zoomError || (classed && (style.classes ?? []).length === 0);
  const title = (info?.labels?.[lang] || info?.labels?.en || company?.labels?.[lang] || company?.labels?.en || platformLayer?.displayName || ref);

  if (!info) {
    return (
      <Drawer title={t('styles.title')} onClose={cancel}>
        <div className="p-4 text-xs text-gn-muted flex items-center gap-2">
          {infoError ? t('styles.load_failed') : (<><Loader2 size={14} className="animate-spin" /> {t('styles.loading')}</>)}
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer title={`${t('styles.title')}: ${title}`} onClose={cancel}
            footer={(
              <div className="flex flex-wrap items-center justify-end gap-2 w-full">
                {tab === 'mine' && info.mine && (
                  <button type="button" className="mr-auto px-2 py-1.5 text-xs rounded hover:bg-gn-tertiary disabled:opacity-50" disabled={busy}
                          onClick={() => resetMine.mutate()} title={t('styles.reset_hint')}>
                    {t('styles.reset')}
                  </button>
                )}
                <button type="button" className="px-3 py-1.5 text-xs rounded hover:bg-gn-tertiary" onClick={cancel}>{t('buttons.cancel')}</button>
                {info.canEditMain && (
                  <button type="button" className={`px-3 py-1.5 text-xs rounded disabled:opacity-50 ${tab === 'main' ? 'bg-gn-blue text-gn-inverse' : 'border border-gn-blue text-gn-blue'}`}
                          disabled={busy || invalid} onClick={() => saveMain.mutate()} title={t('styles.save_main_hint')}>
                    {saveMain.isPending ? '…' : t('styles.save_main')}
                  </button>
                )}
                {info.canKeepView && tab === 'mine' && (
                  <button type="button" className="px-3 py-1.5 text-xs rounded bg-gn-blue text-gn-inverse disabled:opacity-50"
                          disabled={busy || invalid} onClick={() => saveMine.mutate()}>
                    {saveMine.isPending ? '…' : t('styles.save_mine')}
                  </button>
                )}
              </div>
            )}>
      {/* whose look */}
      {(info.canKeepView || info.canEditMain) && (
        <section className="space-y-1">
          <div className="grid grid-cols-2 gap-1 p-0.5 rounded bg-gn-tertiary" role="tablist">
            {info.canKeepView && (
              <button type="button" role="tab" aria-selected={tab === 'mine'} onClick={() => chooseTab('mine')}
                      className={`px-2 py-1 text-[11px] rounded ${tab === 'mine' ? 'bg-gn-surface shadow font-semibold' : ''}`}>
                {t('styles.tab_mine')}
              </button>
            )}
            {info.canEditMain && (
              <button type="button" role="tab" aria-selected={tab === 'main'} onClick={() => chooseTab('main')}
                      className={`px-2 py-1 text-[11px] rounded ${tab === 'main' ? 'bg-gn-surface shadow font-semibold' : ''}`}>
                {t('styles.tab_main')}
              </button>
            )}
          </div>
          <p className="text-[10px] text-gn-muted">
            {tab === 'mine' ? (info.mine ? t('styles.mine_hint_kept') : t('styles.mine_hint_new')) : t('styles.main_hint')}
          </p>
          {info.mainEditable === false && <p className="text-[10px] text-gn-warning">{t('styles.main_geoserver')}</p>}
        </section>
      )}

      {/* how it looks */}
      <section className="space-y-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gn-muted">{t('styles.how')}</p>
        <div className="grid grid-cols-5 gap-1">
          {modes.map((m) => (
            <button key={m} type="button" onClick={() => chooseMode(m)} title={t(`styles.mode_${m}_hint`)} aria-pressed={mode === m}
                    className={`flex flex-col items-center gap-1 p-1.5 rounded border text-[10px] leading-tight ${mode === m ? 'border-gn-blue bg-gn-tertiary' : 'border-gn-light'}`}>
              <ModeIcon mode={m} />
              <span className="text-center">{t(`styles.mode_${m}`)}</span>
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gn-muted">{t(`styles.mode_${mode}_hint`)}</p>
      </section>

      {/* the field it is of */}
      {(mode === 'categories' || mode === 'graduated' || mode === 'proportional') && (
        <section className="space-y-2">
          <div>
            <span className={label}>{t('styles.field')}</span>
            <select className={input} value={by} onChange={(e) => chooseField(e.target.value)}>
              {(mode === 'categories' ? fields : numeric).map((f) => (
                <option key={f.key} value={f.key}>{fieldName(f, lang)}</option>
              ))}
            </select>
            {(mode === 'categories' ? fields : numeric).length === 0 && (
              <p className="text-[10px] text-gn-danger mt-0.5">{mode === 'categories' ? t('styles.no_fields') : t('styles.no_number_fields')}</p>
            )}
            {stats && (
              <p className="text-[10px] text-gn-muted mt-0.5">
                {t('styles.field_summary', { count: stats.count, distinct: stats.distinct, empty: stats.empty })}
                {mode === 'categories' && byField && isNumericType(byField.type) && stats.distinct > 12 && (
                  <button type="button" className="ml-1 underline" onClick={() => chooseMode(suggestedMode(byField.type, stats.distinct))}>
                    {t('styles.try_graduated')}
                  </button>
                )}
              </p>
            )}
            {loadingStats && <p className="text-[10px] text-gn-muted mt-0.5 flex items-center gap-1"><Loader2 size={10} className="animate-spin" />{t('styles.reading')}</p>}
          </div>

          {mode === 'categories' && (
            <div className="flex items-end gap-2">
              <div className="w-20">
                <span className={label}>{t('styles.top')}</span>
                <input type="number" min={1} max={30} className={input} value={top}
                       onChange={(e) => { const n = Math.max(1, Math.min(30, Number(e.target.value) || 1)); setTop(n); rebuildCategories(stats, n); }} />
              </div>
              <label className="flex items-center gap-1 text-[11px] pb-1">
                <input type="checkbox" checked={!!style.otherFill}
                       onChange={(e) => set({ otherFill: e.target.checked ? DEFAULT_OTHER : null })} />
                {t('styles.other_shown')}
              </label>
              {style.otherFill && <input type="color" value={style.otherFill} onChange={(e) => set({ otherFill: e.target.value })} className="h-6 w-8 pb-1" />}
            </div>
          )}

          {mode === 'graduated' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className={label}>{t('styles.method')}</span>
                <select className={input} value={style.method ?? 'quantile'}
                        onChange={(e) => {
                          const m = e.target.value;
                          if (m === 'manual') set({ method: m });
                          else rebuildGraduated(stats, m, style.classCount ?? 5, style.ramp ?? 'blues', !!style.reverse);
                        }}>
                  {METHODS.map((m) => <option key={m} value={m}>{t(`styles.method_${m}`)}</option>)}
                </select>
              </div>
              <div>
                <span className={label}>{t('styles.classes_count', { count: style.classCount ?? 5 })}</span>
                <input type="range" min={3} max={9} value={style.classCount ?? 5} className="w-full"
                       onChange={(e) => rebuildGraduated(stats, style.method && style.method !== 'manual' ? style.method : 'quantile',
                         Number(e.target.value), style.ramp ?? 'blues', !!style.reverse)} />
              </div>
            </div>
          )}

          {classed && (
            <div>
              <div className="flex items-center justify-between">
                <span className={label}>{mode === 'categories' ? t('styles.palette') : t('styles.ramp')}</span>
                {mode === 'graduated' && (
                  <label className="flex items-center gap-1 text-[11px]">
                    <input type="checkbox" checked={!!style.reverse}
                           onChange={(e) => rebuildGraduated(stats, style.method && style.method !== 'manual' ? style.method : 'quantile',
                             style.classCount ?? 5, style.ramp ?? 'blues', e.target.checked)} />
                    {t('styles.reverse')}
                  </label>
                )}
              </div>
              <div className="grid grid-cols-2 gap-1">
                {RAMPS.filter((r) => rampKinds.includes(r.kind)).map((r) => (
                  <button key={r.name} type="button" title={r.safe ? t('styles.colour_safe') : r.name}
                          onClick={() => (mode === 'categories'
                            ? rebuildCategories(stats, top, r.name)
                            : rebuildGraduated(stats, style.method && style.method !== 'manual' ? style.method : 'quantile',
                              style.classCount ?? 5, r.name, !!style.reverse))}
                          className={`flex items-stretch h-4 w-full p-0 rounded overflow-hidden border ${style.ramp === r.name ? 'border-gn-blue ring-1 ring-gn-blue' : 'border-gn-light'}`}>
                    {coloursOf(r.name, mode === 'categories' ? 8 : 7, mode === 'graduated' && !!style.reverse).map((c, i) => (
                      <span key={i} className="block flex-1 h-full" style={{ background: c }} />
                    ))}
                  </button>
                ))}
              </div>
            </div>
          )}

          {classed && (style.classes ?? []).length > 0 && (
            <div className="border border-gn-light rounded">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gn-muted">
                    <th className="w-7" />
                    <th className="text-left font-normal px-1">{mode === 'categories' ? t('styles.value') : t('styles.range')}</th>
                    <th className="text-left font-normal px-1">{t('styles.legend_label')}</th>
                    {mode === 'categories' && <th className="w-12" />}
                  </tr>
                </thead>
                <tbody>
                  {(style.classes ?? []).map((c, i) => (
                    <tr key={i} className="border-t border-gn-light">
                      <td className="px-1"><input type="color" value={c.fill ?? DEFAULT_FILL} className="h-5 w-6" aria-label={classLabel(c, lang)}
                                                  onChange={(e) => changeClass(i, { fill: e.target.value })} /></td>
                      <td className="px-1">
                        {mode === 'categories' ? (
                          <span className="truncate block max-w-[7rem]" title={c.equals ?? ''}>
                            {c.equals}
                            <span className="text-gn-muted"> ({stats?.values.find((v) => v.value === c.equals)?.count ?? '·'})</span>
                          </span>
                        ) : style.method === 'manual' ? (
                          <span className="flex items-center gap-0.5">
                            <input type="number" className={`${input} w-16`} value={c.from ?? ''}
                                   onChange={(e) => changeClass(i, { from: Number(e.target.value), labels: { ...c.labels, [lang]: rangeLabel(Number(e.target.value), c.to ?? 0, locale) } })} />
                            <input type="number" className={`${input} w-16`} value={c.to ?? ''}
                                   onChange={(e) => changeClass(i, { to: Number(e.target.value), labels: { ...c.labels, [lang]: rangeLabel(c.from ?? 0, Number(e.target.value), locale) } })} />
                          </span>
                        ) : (
                          <span>{rangeLabel(c.from ?? 0, c.to ?? 0, locale)}</span>
                        )}
                      </td>
                      <td className="px-1">
                        <input className={input} value={classLabel(c, lang)}
                               onChange={(e) => changeClass(i, { labels: { ...(c.labels ?? {}), [lang]: e.target.value } })} />
                      </td>
                      {mode === 'categories' && (
                        <td className="px-1 whitespace-nowrap">
                          <button type="button" className={small} disabled={i === 0} onClick={() => moveClass(i, -1)} title={t('styles.up')}><ArrowUp size={11} /></button>
                          <button type="button" className={small} disabled={i === (style.classes ?? []).length - 1} onClick={() => moveClass(i, 1)} title={t('styles.down')}><ArrowDown size={11} /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {mode === 'proportional' && (
            <div className="grid grid-cols-2 gap-2">
              <Num label={geometry === 'line' ? t('styles.width_min') : t('styles.size_min')} value={style.sizeMin ?? 4} min={1} max={100} onChange={(v) => set({ sizeMin: v })} />
              <Num label={geometry === 'line' ? t('styles.width_max') : t('styles.size_max')} value={style.sizeMax ?? 30} min={1} max={150} onChange={(v) => set({ sizeMax: v })} />
              <Num label={t('styles.value_min')} value={style.valueMin ?? 0} onChange={(v) => set({ valueMin: v })} />
              <Num label={t('styles.value_max')} value={style.valueMax ?? 1} onChange={(v) => set({ valueMax: v })} />
            </div>
          )}
        </section>
      )}

      {mode === 'heatmap' && (
        <section className="space-y-2">
          <Slider label={t('styles.heat_radius', { value: style.heatRadius ?? 15 })} value={style.heatRadius ?? 15} min={2} max={60} onChange={(v) => set({ heatRadius: v })} />
          <Slider label={t('styles.heat_blur', { value: style.heatBlur ?? 15 })} value={style.heatBlur ?? 15} min={0} max={60} onChange={(v) => set({ heatBlur: v })} />
          <div>
            <span className={label}>{t('styles.weight')}</span>
            <select className={input} value={weightBy} onChange={(e) => set({ weightBy: e.target.value || null })}>
              <option value="">{t('styles.weight_none')}</option>
              {numeric.map((f) => <option key={f.key} value={f.key}>{fieldName(f, lang)}</option>)}
            </select>
          </div>
        </section>
      )}

      {/* the symbol, by the geometry */}
      {mode !== 'heatmap' && (
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gn-muted">{t('styles.symbol')}</p>
            <span className="flex items-center gap-0.5" aria-label={t('styles.preview')}>
              {legendItems(style, geometry, lang, '', '').slice(0, 4).map((it, i) => it.kind === 'swatch' ? <StyleSwatch key={i} symbol={it} width={24} height={18} /> : null)}
            </span>
          </div>
          <SymbolControls style={style} geometry={geometry} classed={classed} sized={mode === 'proportional'} clusters={!!info.clusters}
                          numberFields={numeric.map((f) => ({ key: f.key, name: fieldName(f, lang) }))} set={set} />
        </section>
      )}

      {/* labels and zooms */}
      <section className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gn-muted">{t('styles.labels_zooms')}</p>
        {mode !== 'heatmap' && (
          <>
            <div>
              <span className={label}>{t('styles.label_field')}</span>
              <select className={input} value={labelField} onChange={(e) => { touched.current = true; setLabelField(e.target.value); }}>
                <option value="">{t('styles.label_none')}</option>
                {fields.map((f) => <option key={f.key} value={f.key}>{fieldName(f, lang)}</option>)}
              </select>
            </div>
            {labelField && (
              <div className="grid grid-cols-3 gap-2">
                <Num label={t('styles.label_size')} value={style.labelSize ?? 12} min={6} max={48} onChange={(v) => set({ labelSize: v })} />
                <div>
                  <span className={label}>{t('styles.label_colour')}</span>
                  <div className="flex gap-1">
                    <input type="color" value={style.labelColour ?? '#111827'} onChange={(e) => set({ labelColour: e.target.value })} className="h-6 w-7" title={t('styles.label_colour')} />
                    <input type="color" value={style.labelHalo ?? '#ffffff'} onChange={(e) => set({ labelHalo: e.target.value })} className="h-6 w-7" title={t('styles.label_halo')} />
                  </div>
                </div>
                <Num label={t('styles.label_from_zoom')} value={style.labelMinZoom ?? 0} min={0} max={22} onChange={(v) => set({ labelMinZoom: v || null })} />
              </div>
            )}
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <ZoomField label={t('styles.min_zoom')} value={minZoom} onChange={(v) => { touched.current = true; setMinZoom(v); }} now={currentZoom} nowLabel={t('styles.zoom_now')} anyLabel={t('styles.zoom_any')} />
          <ZoomField label={t('styles.max_zoom')} value={maxZoom} onChange={(v) => { touched.current = true; setMaxZoom(v); }} now={currentZoom} nowLabel={t('styles.zoom_now')} anyLabel={t('styles.zoom_any')} />
        </div>
        {zoomError && <p className="text-[10px] text-gn-danger">{t('styles.zoom_error')}</p>}
        <p className="text-[10px] text-gn-muted">{t('styles.zoom_hint', { zoom: currentZoom() })}</p>
      </section>
    </Drawer>
  );
};

/* ── pieces ── */

const Drawer: React.FC<{ title: string; onClose: () => void; footer?: React.ReactNode; children: React.ReactNode }> = ({ title, onClose, footer, children }) => {
  const { t } = useTranslation();
  // Rendered into <body>: the layer panel it is opened from is translated and blurred, which makes a `fixed`
  // child sit inside that panel and be cut off by it instead of standing at the window's right edge.
  return createPortal(
    <div className="fixed top-16 bottom-4 right-3 z-[60] w-[360px] max-w-[calc(100vw-1.5rem)] flex flex-col rounded-lg border border-gn-light bg-gn-surface text-gn-primary shadow-2xl"
         role="dialog" aria-label={title}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-gn-light">
        <p className="text-xs font-semibold truncate">{title}</p>
        <button type="button" className={small} onClick={onClose} aria-label={t('buttons.cancel')}><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">{children}</div>
      {footer && <div className="flex justify-end gap-2 px-3 py-2 border-t border-gn-light">{footer}</div>}
    </div>,
    document.body,
  );
};

const Num: React.FC<{ label: string; value: number; min?: number; max?: number; onChange: (v: number) => void }> = ({ label: text, value, min, max, onChange }) => (
  <div>
    <span className={label}>{text}</span>
    <input type="number" className={input} value={value} min={min} max={max}
           onChange={(e) => {
             let v = Number(e.target.value);
             if (!Number.isFinite(v)) return;
             if (min !== undefined) v = Math.max(min, v);
             if (max !== undefined) v = Math.min(max, v);
             onChange(v);
           }} />
  </div>
);

const ZoomField: React.FC<{ label: string; value: string; onChange: (v: string) => void; now: () => string; nowLabel: string; anyLabel: string }> =
  ({ label: text, value, onChange, now, nowLabel, anyLabel }) => (
    <div>
      <span className={label}>{text}</span>
      <div className="flex gap-1">
        <input type="number" min={0} max={22} step={0.5} className={input} value={value} placeholder={anyLabel}
               onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="px-1.5 text-[10px] border border-gn-light rounded hover:bg-gn-tertiary whitespace-nowrap" onClick={() => onChange(now())}>{nowLabel}</button>
      </div>
    </div>
  );

/** A small picture of each way to look, so the choice is seen rather than read. */
const ModeIcon: React.FC<{ mode: StyleMode }> = ({ mode }) => {
  const w = 34;
  const h = 22;
  if (mode === 'categories') {
    const c = coloursOf('bold', 4);
    return <svg width={w} height={h}>{[[6, 7], [17, 14], [27, 6], [12, 17]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r={3.5} fill={c[i]} />)}</svg>;
  }
  if (mode === 'graduated') {
    const c = coloursOf('blues', 5);
    return <svg width={w} height={h}>{c.map((col, i) => <rect key={i} x={2 + i * 6} y={4} width={6} height={14} fill={col} />)}</svg>;
  }
  if (mode === 'proportional') {
    return <svg width={w} height={h}><circle cx={7} cy={14} r={3} fill={DEFAULT_FILL} opacity={0.8} /><circle cx={17} cy={12} r={5} fill={DEFAULT_FILL} opacity={0.8} /><circle cx={28} cy={11} r={7} fill={DEFAULT_FILL} opacity={0.8} /></svg>;
  }
  if (mode === 'heatmap') {
    return (
      <svg width={w} height={h}>
        <defs><radialGradient id="gx-heat"><stop offset="0%" stopColor="#ff0000" /><stop offset="45%" stopColor="#ffff00" /><stop offset="75%" stopColor="#00ff00" stopOpacity={0.6} /><stop offset="100%" stopColor="#0000ff" stopOpacity={0} /></radialGradient></defs>
        <ellipse cx={17} cy={11} rx={15} ry={10} fill="url(#gx-heat)" />
      </svg>
    );
  }
  return <svg width={w} height={h}><circle cx={9} cy={8} r={3.5} fill={DEFAULT_FILL} /><circle cx={21} cy={14} r={3.5} fill={DEFAULT_FILL} /><circle cx={27} cy={6} r={3.5} fill={DEFAULT_FILL} /></svg>;
};

export default LayerStyleEditor;
